import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import LocalGasStationOutlinedIcon from "@mui/icons-material/LocalGasStationOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import config from "../../../config";
import StatsCard from "../../common/New/StatsCard";
import {
  ChunkLoadingIndicator,
  useProgressiveOverviewData,
} from "../../manageData/Analytics/useProgressiveOverviewData";

const COLORS = {
  blue: "#2867E8",
  green: "#049334",
  amber: "#F5A000",
  red: "#F04438",
  text: "#172033",
  muted: "#718096",
  grid: "#E9EDF3",
};
// Monthly history fetching is intentionally disabled for now. Keep the
// implementation below available for a future requirement; the chart should
// currently stay connected to the same selected-range trip stream as the KPIs.
const LOAD_MONTHLY_HISTORY = false;
const number = (value) => Number.parseFloat(value) || 0;
const vehicleName = (row, index) =>
  row?.vehicleName ||
  row?.deviceName ||
  row?.vehicleNumber ||
  `Vehicle-${index + 1}`;
const parseMinutes = (value) => {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "object")
    return value.minutes != null
      ? number(value.minutes)
      : value.milliseconds != null
        ? number(value.milliseconds) / 60000
        : parseMinutes(value.string);
  const text = String(value).toLowerCase();
  const days = number(text.match(/([\d.]+)\s*d/)?.[1]);
  const hours = number(text.match(/([\d.]+)\s*h/)?.[1]);
  const minutes = number(text.match(/([\d.]+)\s*m(?!s)/)?.[1]);
  const seconds = number(text.match(/([\d.]+)\s*s/)?.[1]);
  if (days || hours || minutes || seconds)
    return days * 1440 + hours * 60 + minutes + seconds / 60;
  const clock = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  return clock
    ? number(clock[1]) * 60 + number(clock[2]) + number(clock[3]) / 60
    : number(text);
};
const tripDuration = (row) => {
  for (const value of [
    row?.durationMinutes,
    row?.tripDuration,
    row?.totalDuration,
    row?.duration,
  ]) {
    const parsed = parseMinutes(value);
    if (parsed > 0) return parsed;
  }
  return row?.durationSeconds != null ? number(row.durationSeconds) / 60 : 0;
};
const tripDate = (row) => {
  const raw = row?.date || row?.startDate || row?.timestamp || row?.createdAt;
  if (!raw) return null;
  const time = String(row?.startTime || "");
  const text = String(raw).trim();
  const numericDate = text.match(
    new RegExp("^(\\d{1,4})[./-](\\d{1,2})[./-](\\d{1,4})"),
  );
  let date;
  if (numericDate) {
    const [, first, second, third] = numericDate;
    const year = first.length === 4 ? number(first) : number(third);
    const month = number(second);
    const day = first.length === 4 ? number(third) : number(first);
    date = new Date(year, month - 1, day);
    const timeMatch = time.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (timeMatch) date.setHours(number(timeMatch[1]), number(timeMatch[2]), number(timeMatch[3]), 0);
  } else {
    date = new Date(raw);
  }
  return Number.isNaN(date.getTime()) ? null : date;
};
async function fetchTripRange(organizationId, startDate, endDate, signal) {
  const rows = [];
  let page = 1;
  let totalPages = 1;
  do {
    const url = new URL(`${config.baseURL}/report/trip`);
    const params = { organizationId, dateOption: "custom", customStartDate: startDate.toISOString(), customEndDate: endDate.toISOString(), allVehicles: true, allRoutes: false, allDrivers: false, allStops: false, allSites: false, page, pageSize: 100 };
    Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Weekly trips request failed (${response.status})`);
    const payload = await response.json();
    rows.push(...(Array.isArray(payload?.data) ? payload.data : []));
    totalPages = number(payload?.totalPages || payload?.pagination?.totalPages) || 1;
    page += 1;
  } while (page <= totalPages);
  return rows;
}
const isOnTime = (row) =>
  String(row?.status || "").toLowerCase() === "completed";
const isCancelled = (row) =>
  /cancel/.test(String(row?.status || "").toLowerCase());
const isDelayed = (row) => !isOnTime(row) && !isCancelled(row);
const compact = (value) =>
  new Intl.NumberFormat(undefined, {
    notation: value >= 10000 ? "compact" : "standard",
    maximumFractionDigits: 1,
  }).format(value || 0);

function Panel({ title, progress, action, children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #DDE2EA",
        borderRadius: 1.5,
        overflow: "hidden",
        position: "relative",
        ...sx,
      }}
    >
      <ChunkLoadingIndicator progress={progress} />
      <Box
        sx={{
          minHeight: 44,
          px: 2,
          py: 1.2,
          borderBottom: "1px solid #EEF1F5",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 13, color: COLORS.text, fontWeight: 700 }}>
          {title}
        </Typography>
        {action}
      </Box>
      <Box sx={{ p: 1.5 }}>{children}</Box>
    </Paper>
  );
}
const ChartTooltip = ({ active, payload, label }) =>
  active && payload?.length ? (
    <Paper sx={{ px: 1.2, py: 0.8 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{label}</Typography>
      {payload.map((item) => (
        <Typography
          key={item.dataKey || item.name}
          sx={{ fontSize: 10, color: item.color }}
        >
          {item.name}: {item.value}
        </Typography>
      ))}
    </Paper>
  ) : null;
function SummaryRow({ icon, tint, label, value }) {
  return (
    <Box
      sx={{
        py: 1.3,
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid #EEF1F5",
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 25,
          height: 25,
          borderRadius: "50%",
          bgcolor: tint,
          display: "grid",
          placeItems: "center",
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: 10, color: COLORS.muted, flex: 1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 11, color: COLORS.text, fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function TripAnalytics({
  selectedDateRange = "today",
  customDateRange = null,
}) {
  const { t } = useTranslation();
  const { chartDataStates, chunkProgress, error, isCancelling } =
    useProgressiveOverviewData(selectedDateRange, customDateRange);
  const tripState = chartDataStates.trip_vehicles;
  const runtimeState = chartDataStates.runtime_vehicles;
  const trips = useMemo(() => tripState?.data?.data || [], [tripState]);
  const runtimeRows = useMemo(
    () => runtimeState?.data?.data || [],
    [runtimeState],
  );
  const [weeklyTrips, setWeeklyTrips] = useState([]);
  const [weeklyDaysLoaded, setWeeklyDaysLoaded] = useState(0);
  const [weeklyError, setWeeklyError] = useState(null);
  const [monthlyTripCounts, setMonthlyTripCounts] = useState(
    Array.from({ length: 12 }, () => 0),
  );
  const [monthsLoaded, setMonthsLoaded] = useState(0);
  const [monthlyError, setMonthlyError] = useState(null);
  const [isMonthChartVisible, setIsMonthChartVisible] = useState(false);
  const monthChartRef = useRef(null);
  const currentWeekday = new Date().getDay();
  const weeklyDaysExpected = currentWeekday + 1;
  const currentMonthIndex = new Date().getMonth();
  const monthlyChunks = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const chunks = [];
    for (let monthIndex = 0; monthIndex <= now.getMonth(); monthIndex += 1) {
      const monthEnd = new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);
      const rangeEnd = monthIndex === now.getMonth() ? now : monthEnd;
      let cursor = new Date(year, monthIndex, 1, 0, 0, 0, 0);
      while (cursor <= rangeEnd) {
        const start = new Date(cursor);
        const end = new Date(cursor);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        if (end > rangeEnd) end.setTime(rangeEnd.getTime());
        chunks.push({ monthIndex, start, end });
        cursor = new Date(end);
        cursor.setMilliseconds(cursor.getMilliseconds() + 1);
      }
    }
    return chunks;
  }, []);
  const monthlyRequestsExpected = monthlyChunks.length;
  const progress = chunkProgress.trip_vehicles;
  const loading = !error && (isCancelling || tripState?.loading || !tripState);

  useEffect(() => {
    if (!LOAD_MONTHLY_HISTORY) return undefined;
    const target = monthChartRef.current;
    if (!target) return undefined;
    if (!("IntersectionObserver" in window)) {
      setIsMonthChartVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsMonthChartVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const userState = JSON.parse(localStorage.getItem("userState") || "null");
    const organizationId = userState?.user?.organizationId;
    if (!organizationId) return () => controller.abort();

    setWeeklyTrips([]);
    setWeeklyDaysLoaded(0);
    setWeeklyError(null);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - today.getDay());
    Array.from({ length: today.getDay() + 1 }, (_, index) => {
      const start = new Date(sunday);
      start.setDate(sunday.getDate() + index);
      const end = new Date(start);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }).forEach(({ start, end }) => {
      fetchTripRange(organizationId, start, end, controller.signal)
        .then((rows) => {
          // These daily requests have non-overlapping date ranges. Preserve
          // every row because distinct trips can share the same visible fields.
          // The request range is also the authoritative weekday. Some backend
          // placeholder/in-progress rows do not carry a parseable `date`.
          const weekday = start.getDay();
          setWeeklyTrips((current) => [
            ...current,
            ...rows.map((row) => ({ ...row, __analyticsWeekday: weekday })),
          ]);
        })
        .catch((requestError) => {
          if (requestError.name !== "AbortError") setWeeklyError(requestError.message);
        })
        .finally(() => {
          if (!controller.signal.aborted) setWeeklyDaysLoaded((count) => count + 1);
        });
    });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!LOAD_MONTHLY_HISTORY || !isMonthChartVisible) return undefined;
    const controller = new AbortController();
    const userState = JSON.parse(localStorage.getItem("userState") || "null");
    const organizationId = userState?.user?.organizationId;
    if (!organizationId) return () => controller.abort();

    setMonthlyTripCounts(Array.from({ length: 12 }, () => 0));
    setMonthsLoaded(0);
    setMonthlyError(null);
    monthlyChunks.forEach(({ monthIndex, start, end }) => {
      fetchTripRange(organizationId, start, end, controller.signal)
        .then((rows) => {
          setMonthlyTripCounts((current) => {
            const next = [...current];
            next[monthIndex] += rows.length;
            return next;
          });
        })
        .catch((requestError) => {
          if (requestError.name !== "AbortError") {
            setMonthlyError(requestError.message);
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setMonthsLoaded((count) => count + 1);
          }
        });
    });

    return () => controller.abort();
  }, [isMonthChartVisible, monthlyChunks]);

  const weekdayTrips = weeklyDaysLoaded > 0 ? weeklyTrips : trips;
  const analytics = useMemo(() => {
    const total = trips.length;
    const onTime = trips.filter(isOnTime).length;
    const cancelled = trips.filter(isCancelled).length;
    const delayed = trips.filter(isDelayed).length;
    const durations = trips.map(tripDuration).filter((value) => value > 0);
    const averageDuration = durations.length
      ? durations.reduce((sum, value) => sum + value, 0) / durations.length
      : 0;
    const longestDuration = durations.length ? Math.max(...durations) : 0;
    const averageStops = total
      ? trips.reduce(
          (sum, row) =>
            sum +
            number(
              row.noOfStopsCovered ??
                row.stopsCovered ??
                row.stopCount ??
                row.totalStops,
            ),
          0,
        ) / total
      : 0;
    const tripDistance = trips.reduce(
      (sum, row) =>
        sum +
        number(
          row.distance ??
            row.distanceTravelled ??
            row.totalDistance ??
            row.noOfKmsCovered,
        ),
      0,
    );
    const runtimeDistance = runtimeRows.reduce(
      (sum, row) =>
        sum +
        number(
          row.noOfKmsCovered ??
            row.distanceCovered ??
            row.totalDistance ??
            row.distance,
        ),
      0,
    );
    const distance = tripDistance > 0 ? tripDistance : runtimeDistance;
    const fuel = trips.reduce(
      (sum, row) =>
        sum + number(row.fuelConsumed ?? row.fuel ?? row.fuelConsumption),
      0,
    );
    const vehicles = new Map();
    const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
      (day) => ({ day, trips: 0 }),
    );
    const months = Array.from({ length: 12 }, (_, index) => ({
      month: new Date(2000, index, 1).toLocaleString(undefined, {
        month: "short",
      }),
      trips: 0,
    }));
    trips.forEach((row, index) => {
      vehicles.set(
        vehicleName(row, index),
        (vehicles.get(vehicleName(row, index)) || 0) + 1,
      );
      const date = tripDate(row);
      if (date) months[date.getMonth()].trips += 1;
    });
    if (LOAD_MONTHLY_HISTORY && monthsLoaded > 0) {
      months.forEach((month, index) => {
        if (index <= currentMonthIndex) month.trips = monthlyTripCounts[index];
      });
    }
    if (selectedDateRange === "today") {
      months[currentMonthIndex].trips = total;
    }
    weekdayTrips.forEach((row) => {
      const weekday = Number.isInteger(row.__analyticsWeekday)
        ? row.__analyticsWeekday
        : tripDate(row)?.getDay();
      if (weekday != null) weekdays[weekday].trips += 1;
    });
    // Keep the current-day bar and the Trips KPI on the same live snapshot.
    // The separate weekly request can finish a moment before the progressive
    // today stream and otherwise differ when a new trip arrives between calls.
    if (selectedDateRange === "today") {
      weekdays[currentWeekday].trips = total;
    }
    const durationBuckets = [
      ["0–15 mins", 0, 15],
      ["15–30 mins", 15, 30],
      ["30–45 mins", 30, 45],
      ["45–60 mins", 45, 60],
      ["60–90 mins", 60, 90],
      ["90–120 mins", 90, 120],
      ["120+ mins", 120, Infinity],
    ].map(([label, min, max]) => ({ label, min, max, trips: 0 }));
    durations.forEach((duration) => {
      durationBuckets.find(
        (bucket) => duration >= bucket.min && duration < bucket.max,
      ).trips += 1;
    });
    return {
      total,
      onTime,
      delayed,
      cancelled,
      averageDuration,
      longestDuration,
      averageStops,
      distance,
      tripDistance,
      fuel,
      onTimePercent: total ? (onTime / total) * 100 : 0,
      vehicles: [...vehicles]
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5),
      weekdays,
      months,
      durationBuckets,
    };
  }, [
    currentMonthIndex,
    currentWeekday,
    monthlyTripCounts,
    monthsLoaded,
    runtimeRows,
    selectedDateRange,
    trips,
    weekdayTrips,
  ]);
  const statusData = [
    { name: t("On Time"), value: analytics.onTime, color: COLORS.green },
    { name: t("Delayed"), value: analytics.delayed, color: COLORS.amber },
    { name: t("Cancelled"), value: analytics.cancelled, color: COLORS.red },
  ];
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().toLocaleString(undefined, { month: "long" });
  const localizedWeekdays = analytics.weekdays.map((row) => ({
    ...row,
    day: t(row.day),
  }));
  const localizedMonths = analytics.months.map((row) => ({
    ...row,
    month: t(row.month),
  }));
  const localizedDurationBuckets = analytics.durationBuckets.map((row) => ({
    ...row,
    label: t(row.label),
  }));

  return (
    <Box sx={{ color: COLORS.text }}>
      {error && (
        <Typography color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(5, 1fr)",
          },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <StatsCard
          title={t("Trips")}
          value={String(analytics.total)}
          icon={AltRouteOutlinedIcon}
          tone="blue"
          loading={loading}
        />
        <StatsCard
          title={t("On-Time Trips")}
          value={String(analytics.onTime)}
          icon={CheckCircleOutlineOutlinedIcon}
          tone="green"
          loading={loading}
        />
        <StatsCard
          title={t("On-Time Performance")}
          value={`${analytics.onTimePercent.toFixed(0)}%`}
          icon={TimerOutlinedIcon}
          tone="amber"
          loading={loading}
        />
        <StatsCard
          title={t("Average Stops per Trip")}
          value={analytics.averageStops.toFixed(1)}
          icon={LocationOnOutlinedIcon}
          tone="purple"
          loading={loading}
        />
        <StatsCard
          title={t("Longest Trip")}
          value={`${(analytics.longestDuration / 60).toFixed(1)} hrs`}
          icon={AccessTimeOutlinedIcon}
          tone="cyan"
          loading={loading}
        />
      </Box>
      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <CircularProgress size={15} />
          <Typography sx={{ fontSize: 11, color: COLORS.muted }}>
            {t("Loading analytics...")}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: ".78fr 1.45fr 1.55fr" },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Panel title={t("Trips by Status")} progress={progress}>
          <ResponsiveContainer width="100%" height={165}>
            <PieChart>
              <Pie
                data={statusData}
                dataKey="value"
                nameKey="name"
                innerRadius={54}
                outerRadius={70}
                startAngle={90}
                endAngle={-270}
                paddingAngle={1}
              >
                {statusData.map((item) => (
                  <Cell key={item.name} fill={item.color} />
                ))}
                <Label
                  value={`${analytics.onTimePercent.toFixed(1)}%`}
                  position="center"
                  fill={COLORS.text}
                  fontSize={18}
                  fontWeight={700}
                />
              </Pie>
              <Tooltip content={<ChartTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <Box sx={{ display: "grid", gap: 0.65 }}>
            {statusData.map((item) => (
              <Box
                key={item.name}
                sx={{ display: "flex", alignItems: "center", gap: 0.7 }}
              >
                <Box sx={{ width: 7, height: 7, bgcolor: item.color }} />
                <Typography sx={{ fontSize: 9, color: COLORS.muted, flex: 1 }}>
                  {item.name}
                </Typography>
                <Typography sx={{ fontSize: 9 }}>
                  {item.value} (
                  {analytics.total
                    ? ((item.value / analytics.total) * 100).toFixed(1)
                    : 0}
                  %)
                </Typography>
              </Box>
            ))}
          </Box>
        </Panel>
        <Panel title={t("Trips by Vehicle")} progress={progress}>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart
              data={analytics.vehicles}
              layout="vertical"
              margin={{ top: 8, right: 25, left: 12, bottom: 12 }}
            >
              <CartesianGrid horizontal={false} stroke={COLORS.grid} />
              <XAxis type="number" tick={{ fontSize: 9 }} />
              <YAxis
                type="category"
                dataKey="name"
                width={70}
                tick={{ fontSize: 9 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} />
              <Bar
                dataKey="count"
                name={t("Trips")}
                fill={COLORS.blue}
                radius={[0, 2, 2, 0]}
                barSize={14}
                label={{ position: "right", fontSize: 9, fill: COLORS.text }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel
          title={t("Trips by Day of Week")}
          progress={
            weeklyDaysLoaded < weeklyDaysExpected
              ? {
                  percentComplete: Math.round(
                    (weeklyDaysLoaded / weeklyDaysExpected) * 100,
                  ),
                }
              : null
          }
        >
          {weeklyError && !weeklyTrips.length && (
            <Typography color="error" sx={{ fontSize: 10, mb: 1 }}>
              {t("Unable to load weekly trips")}
            </Typography>
          )}
          <ResponsiveContainer width="100%" height={230}>
            <BarChart
              data={localizedWeekdays}
              margin={{ top: 15, right: 8, left: -20, bottom: 0 }}
            >
              <CartesianGrid vertical={false} stroke={COLORS.grid} />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 9 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 9 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} />
              <Bar
                dataKey="trips"
                name={t("Trips")}
                radius={[2, 2, 0, 0]}
                barSize={22}
                minPointSize={3}
                label={{ position: "top", fontSize: 9, fill: COLORS.text }}
              >
                {localizedWeekdays.map((entry, index) => (
                  <Cell
                    key={entry.day}
                    fill={index <= currentWeekday ? COLORS.blue : "#D7DCE5"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.6fr 1fr" },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Panel title={t("Trips Duration Distribution")} progress={progress}>
          <ResponsiveContainer width="100%" height={235}>
            <BarChart
              data={localizedDurationBuckets}
              margin={{ top: 15, right: 8, left: -20, bottom: 0 }}
            >
              <CartesianGrid vertical={false} stroke={COLORS.grid} />
              <XAxis
                dataKey="label"
                interval={0}
                tick={{ fontSize: 8 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 9 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} />
              <Bar
                dataKey="trips"
                name={t("Trips")}
                fill={COLORS.blue}
                radius={[2, 2, 0, 0]}
                barSize={24}
                label={{ position: "top", fontSize: 9, fill: COLORS.text }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel
          title={t("Trip Performance Summary")}
          progress={
            analytics.tripDistance > 0
              ? progress
              : chunkProgress.runtime_vehicles
          }
        >
          <SummaryRow
            label={t("On-Time Performance")}
            value={`${analytics.onTimePercent.toFixed(0)}%`}
            tint="#E6FAEF"
            icon={
              <CheckCircleOutlineOutlinedIcon
                sx={{ fontSize: 15, color: COLORS.green }}
              />
            }
          />
          <SummaryRow
            label={t("Average Trip Duration")}
            value={`${analytics.averageDuration.toFixed(0)} min`}
            tint="#E9F2FF"
            icon={
              <AccessTimeOutlinedIcon
                sx={{ fontSize: 15, color: COLORS.blue }}
              />
            }
          />
          <SummaryRow
            label={t("Total Distance Travelled")}
            value={`${compact(analytics.distance)} km`}
            tint="#EEF2F7"
            icon={
              <DirectionsCarOutlinedIcon
                sx={{ fontSize: 15, color: COLORS.muted }}
              />
            }
          />
          <SummaryRow
            label={t("Fuel Consumed")}
            value={`${compact(analytics.fuel)} L`}
            tint="#FFF1DF"
            icon={
              <LocalGasStationOutlinedIcon
                sx={{ fontSize: 15, color: COLORS.amber }}
              />
            }
          />
        </Panel>
      </Box>
      <Box ref={monthChartRef} sx={{ width: { xs: "100%", lg: "42%" } }}>
        <Panel
          title={t("Trips by Month")}
          progress={
            LOAD_MONTHLY_HISTORY &&
            isMonthChartVisible &&
            monthsLoaded < monthlyRequestsExpected
              ? {
                  percentComplete: Math.round(
                    (monthsLoaded / monthlyRequestsExpected) * 100,
                  ),
                }
              : null
          }
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              <Select
                size="small"
                value={currentYear}
                sx={{ height: 30, fontSize: 10 }}
              >
                <MenuItem value={currentYear}>{currentYear}</MenuItem>
              </Select>
              <Select
                size="small"
                value={currentMonth}
                sx={{ height: 30, fontSize: 10 }}
              >
                <MenuItem value={currentMonth}>{t(currentMonth)}</MenuItem>
              </Select>
            </Box>
          }
        >
          {monthlyError && monthsLoaded === 0 && (
            <Typography color="error" sx={{ fontSize: 10, mb: 1 }}>
              {t("Unable to load monthly trips")}
            </Typography>
          )}
          <ResponsiveContainer width="100%" height={230}>
            <BarChart
              data={localizedMonths}
              margin={{ top: 15, right: 8, left: -20, bottom: 0 }}
            >
              <CartesianGrid vertical={false} stroke={COLORS.grid} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 8 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 9 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} />
              <Bar
                dataKey="trips"
                name={t("Trips")}
                radius={[2, 2, 0, 0]}
                barSize={16}
              >
                {localizedMonths.map((entry, index) => (
                  <Cell
                    key={entry.month}
                    fill={
                      index <= currentMonthIndex ? COLORS.blue : "#D7DCE5"
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </Box>
    </Box>
  );
}
